import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FilePdf, Printer, FloppyDisk, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { openPdfInBrowser, printPdf } from '../utils/pdfUtils';

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const pad = (n) => String(n).padStart(2, '0');
export const authCfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
export const fmtDMY = (d) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : '—');

export const CleanerSelect = ({ cleaners, value, onChange, el, testId }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className="h-8 border border-zinc-300 bg-white px-2 text-xs" data-testid={testId}>
    <option value="">{el ? 'Όλες οι καθαρίστριες (All Cleaners)' : 'All Cleaners'}</option>
    {cleaners.map((c) => <option key={c.cleaner_id} value={c.cleaner_id}>{c.name}</option>)}
  </select>
);

export const PdfPrintButtons = ({ label, path, params, testId, disabled }) => {
  const run = (fn) => async () => {
    try { await fn(path, typeof params === 'function' ? await params() : params); }
    catch (e) { toast.error(e.message || 'PDF error'); }
  };
  return (
    <div className="inline-flex border border-zinc-300 bg-white" data-testid={testId}>
      <Button variant="ghost" size="sm" disabled={disabled} className="rounded-none h-8 px-2 text-xs" onClick={run(openPdfInBrowser)} data-testid={`${testId}-pdf`}>
        <FilePdf size={14} className="mr-1" />{label}
      </Button>
      <Button variant="ghost" size="sm" disabled={disabled} className="rounded-none h-8 px-2 border-l border-zinc-200" onClick={run(printPdf)} title="Print" data-testid={`${testId}-print`}>
        <Printer size={14} />
      </Button>
    </div>
  );
};

export const Check = ({ checked, onChange, label, testId }) => (
  <label className="flex items-center gap-1.5 text-xs text-zinc-600 cursor-pointer whitespace-nowrap">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="accent-zinc-900" data-testid={testId} />{label}
  </label>
);

export const NeedsCheckTag = ({ testId }) => (
  <span className="ml-1.5 align-middle text-[8px] font-bold tracking-wide text-amber-700 bg-amber-50 border border-amber-300 px-1 py-px whitespace-nowrap" data-testid={testId}>NO JOB RECORDED – NEEDS CHECK</span>
);

// Row visibility + manual selection + saved selections (shared by weekly and annual views).
export const useRowSelection = (dataRows, search) => {
  const [onlyWith, setOnlyWith] = useState(true);
  const [withoutOnly, setWithoutOnly] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [showSelected, setShowSelected] = useState(false);
  const [saved, setSaved] = useState([]);
  const [loadedId, setLoadedId] = useState('');
  const [saveOpen, setSaveOpen] = useState(false);
  const reloadSaved = useCallback(() => axios.get(`${API}/cleaning/schedule/selections`, authCfg()).then((r) => setSaved(r.data || [])).catch(() => {}), []);
  useEffect(() => { reloadSaved(); }, [reloadSaved]);

  const allRows = useMemo(() => (dataRows || []).filter((r) => !search || (r.name || '').toLowerCase().includes(search.toLowerCase())), [dataRows, search]);
  const rows = useMemo(() => allRows.filter((r) => (showSelected ? selected.has(r.row_key)
    : (!onlyWith || r.has_entries) && (!withoutOnly || !r.has_entries))), [allRows, showSelected, selected, onlyWith, withoutOnly]);
  const toggle = (k) => setSelected((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const setWith = (v) => { setOnlyWith(v); if (v) setWithoutOnly(false); };
  const setWithout = (v) => { setWithoutOnly(v); if (v) setOnlyWith(false); };
  const actions = {
    selectAll: () => setSelected(new Set([...selected, ...rows.map((r) => r.row_key)])),
    deselectAll: () => { setSelected(new Set()); setShowSelected(false); setLoadedId(''); },
    selectWithout: () => { setOnlyWith(false); setSelected(new Set(allRows.filter((r) => !r.has_entries).map((r) => r.row_key))); },
    setShowSelected,
  };
  const onLoad = (id) => {
    setLoadedId(id);
    const s = saved.find((x) => x.selection_id === id);
    if (s) { setSelected(new Set(s.row_keys)); setShowSelected(true); }
  };
  const onSave = async (name, el) => {
    try {
      const r = await axios.post(`${API}/cleaning/schedule/selections`, { name, row_keys: [...selected] }, authCfg());
      toast.success(el ? 'Η επιλογή αποθηκεύτηκε' : 'Selection saved'); setSaveOpen(false); await reloadSaved(); setLoadedId(r.data.selection_id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const onDelete = async (el) => {
    try { await axios.delete(`${API}/cleaning/schedule/selections/${loadedId}`, authCfg()); setLoadedId(''); reloadSaved(); toast.success(el ? 'Διαγράφηκε' : 'Deleted'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const printParams = async (base) => {
    const b = { ...base, search, only_with_entries: onlyWith && !withoutOnly, only_without_entries: withoutOnly };
    if (!selected.size) return b;
    const keys = (dataRows || []).filter((r) => selected.has(r.row_key)).map((r) => r.row_key);
    const r = await axios.post(`${API}/cleaning/schedule/print-requests`, { row_keys: keys }, authCfg());
    return { ...b, search: '', req_id: r.data.req_id };
  };
  return { rows, selected, toggle, onlyWith, withoutOnly, setWith, setWithout, showSelected, actions, saved, loadedId, onLoad, onSave, onDelete, saveOpen, setSaveOpen, printParams };
};

export const JobFilterChecks = ({ sel, el }) => (
  <>
    <Check checked={sel.onlyWith} onChange={sel.setWith} label={el ? 'Μόνο με εργασίες (Only with Jobs)' : 'Only with Jobs'} testId="schedule-only-with-entries" />
    <Check checked={sel.withoutOnly} onChange={sel.setWithout} label="Without Jobs" testId="schedule-without-jobs" />
  </>
);

const SaveSelectionDialog = ({ open, initial, onClose, onSave, el }) => {
  const [name, setName] = useState(initial);
  useEffect(() => { if (open) setName(initial); }, [open, initial]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-sm" aria-describedby={undefined} data-testid="schedule-save-selection-dialog">
        <DialogHeader><DialogTitle className="text-base">Save Selection</DialogTitle></DialogHeader>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={el ? 'Όνομα λίστας' : 'List name'} className="rounded-none" data-testid="schedule-save-selection-name" />
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>{el ? 'Ακύρωση' : 'Cancel'}</Button>
          <Button className="rounded-none bg-zinc-950" onClick={() => onSave(name, el)} data-testid="schedule-save-selection-confirm">{el ? 'Αποθήκευση' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const SelectionBar = ({ sel, el }) => (
  <div className="flex flex-wrap items-center gap-1.5 mb-2 text-xs" data-testid="schedule-selection-bar">
    <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold mr-1">{el ? 'Επιλογή' : 'Selection'}: <b className="text-zinc-800" data-testid="schedule-selected-count">{sel.selected.size}</b></span>
    <Button variant="outline" size="sm" className="rounded-none h-7 text-xs" onClick={sel.actions.selectAll} data-testid="schedule-select-all">Select All</Button>
    <Button variant="outline" size="sm" className="rounded-none h-7 text-xs" onClick={sel.actions.deselectAll} data-testid="schedule-deselect-all">Deselect All</Button>
    <Button variant="outline" size="sm" className="rounded-none h-7 text-xs" onClick={sel.actions.selectWithout} data-testid="schedule-select-without">Select Only Without Jobs</Button>
    <Check checked={sel.showSelected} onChange={sel.actions.setShowSelected} label="Show Selected Only" testId="schedule-show-selected-only" />
    <span className="mx-1 h-5 w-px bg-zinc-200" />
    <select value={sel.loadedId} onChange={(e) => sel.onLoad(e.target.value)} className="h-7 border border-zinc-300 bg-white px-2 text-xs max-w-[200px]" data-testid="schedule-load-selection">
      <option value="">Load Saved Selection…</option>
      {sel.saved.map((s) => <option key={s.selection_id} value={s.selection_id}>{s.name} ({s.row_keys.length})</option>)}
    </select>
    <Button variant="outline" size="sm" className="rounded-none h-7 text-xs" onClick={() => sel.setSaveOpen(true)} disabled={!sel.selected.size} data-testid="schedule-save-selection"><FloppyDisk size={13} className="mr-1" />Save Selection</Button>
    {sel.loadedId && <button onClick={() => sel.onDelete(el)} className="p-1 text-red-600 hover:bg-red-50" title={el ? 'Διαγραφή αποθηκευμένης επιλογής' : 'Delete saved selection'} data-testid="schedule-delete-selection"><Trash size={13} /></button>}
    <SaveSelectionDialog open={sel.saveOpen} initial={sel.saved.find((s) => s.selection_id === sel.loadedId)?.name || ''} el={el} onClose={() => sel.setSaveOpen(false)} onSave={sel.onSave} />
  </div>
);
